import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneCall, PhoneOff, PhoneForwarded, Trophy, CalendarDays, Percent, Users, Target, Send, Download } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

const UITKOMST_LABEL = {
  gesproken: "Gesproken", geen_gehoor: "Geen gehoor",
  later_terugbellen: "Later terugbellen", sample: "Sample toesturen",
  afspraak: "Afspraak gemaakt", deal: "Deal gesloten",
};
const UITKOMST_VARIANT = {
  gesproken: "secondary", geen_gehoor: "outline",
  later_terugbellen: "default", sample: "secondary", afspraak: "default", deal: "default",
};
const DEAL_GEEL = "#eab308";
const OVERIG_GRIJS = "#94a3b8";
const CONV_BLAUW = "#0ea5e9";

const Kpi = ({ icon: Icon, label, value, sub, testid, highlight }) => (
  <div className="stat-card" data-testid={testid}>
    <Icon className={`h-5 w-5 ${highlight ? "text-accent" : "text-muted-foreground"}`} />
    <div className="mt-6">
      <div className="font-display text-4xl font-medium">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  </div>
);

export default function Belactiviteit() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/belactiviteit").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const exporteer = async (params = {}) => {
    await downloadFile("/belactiviteit/export", "belactiviteit.csv", { params });
  };

  const kpi = data?.kpi;
  const recent = data?.recent || [];
  const perMed = data?.per_medewerker || [];

  return (
    <div className="space-y-8" data-testid="belactiviteit-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm text-muted-foreground">Verkoop</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Belactiviteit</h1>
          <p className="text-sm text-muted-foreground mt-1">Overzicht van gelogde belletjes, deals en conversie.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exporteer({ uitkomst: "sample" })} data-testid="export-samples-btn">
            <Download className="h-4 w-4 mr-1" /> Samples (CSV)
          </Button>
          <Button onClick={() => exporteer()} data-testid="export-belletjes-btn">
            <Download className="h-4 w-4 mr-1" /> Exporteer belletjes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={PhoneCall} label="Gebeld vandaag" testid="kpi-vandaag"
          value={kpi ? kpi.vandaag : "—"} sub={kpi ? `${kpi.deals_vandaag} deals · ${kpi.conversie_vandaag}% conversie` : null} />
        <Kpi icon={Send} label="Sample opsturen vandaag" testid="kpi-samples-vandaag" highlight
          value={kpi ? (kpi.samples_vandaag ?? 0) : "—"} />
        <Kpi icon={CalendarDays} label="Deze week" testid="kpi-week" value={kpi ? kpi.deze_week : "—"} />
        <Kpi icon={CalendarDays} label="Deze maand" testid="kpi-maand" value={kpi ? kpi.deze_maand : "—"} />
        <Kpi icon={Percent} label="Conversie (deals/belletjes)" testid="kpi-conversie" highlight
          value={kpi ? `${kpi.conversie_pct}%` : "—"} sub={kpi ? `${kpi.deals_totaal} deals · ${kpi.totaal} belletjes` : null} />
      </div>

      {kpi && (() => {
        const doel = kpi.beldoel || 20;
        const pct = doel ? Math.min(100, Math.round((kpi.vandaag / doel) * 100)) : 0;
        const behaald = kpi.vandaag >= doel;
        return (
          <Link to="/instellingen/beldoel" className="block bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors" data-testid="beldoel-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" /> Dagdoel
                {behaald && <Badge className="bg-accent text-accent-foreground">Behaald! 🎉</Badge>}
              </div>
              <div className="text-sm text-muted-foreground" data-testid="beldoel-progress-text">
                <span className="font-display text-base text-foreground">{kpi.vandaag}</span> / {doel} belletjes
              </div>
            </div>
            <Progress value={pct} className={behaald ? "[&>div]:bg-accent" : ""} />
          </Link>
        );
      })()}

      <div className="bg-card border border-border rounded-xl p-5" data-testid="bel-trend-chart">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-medium">Trend per week</h2>
          <span className="text-xs text-muted-foreground">Laatste 12 weken · <span style={{ color: DEAL_GEEL }}>geel = deals</span></span>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          {data?.per_week ? (
          <ResponsiveContainer>
            <ComposedChart data={data.per_week} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} tick={{ fontSize: 12 }} stroke={CONV_BLAUW} />
              <Tooltip formatter={(v, n) => [v, n]} labelStyle={{ color: "#111" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="overig" name="Overig (geen deal)" stackId="a" fill={OVERIG_GRIJS} radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="deals" name="Deals" stackId="a" fill={DEAL_GEEL} radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="conversie" name="Conversie %" stroke={CONV_BLAUW} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Laden…</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Per medewerker — modulair, eenvoudig uit te breiden */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="medewerker-table">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-primary" />
            <h2 className="font-display text-2xl font-medium">Per medewerker</h2>
          </div>
          {perMed.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">Nog geen belletjes gelogd.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground text-left border-b border-border">
                <th className="py-2">Medewerker</th><th className="py-2 text-right">Belletjes</th>
                <th className="py-2 text-right">Deals</th><th className="py-2 text-right">Conversie</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {perMed.map((m) => (
                  <tr key={m.eigenaar} data-testid={`med-row-${m.eigenaar}`}>
                    <td className="py-2 font-medium">{m.eigenaar_naam}</td>
                    <td className="py-2 text-right">{m.totaal}</td>
                    <td className="py-2 text-right" style={{ color: DEAL_GEEL }}>{m.deals}</td>
                    <td className="py-2 text-right">{m.conversie}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recente belletjes */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="recent-belletjes">
          <h2 className="font-display text-2xl font-medium mb-4">Recente belletjes</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recent.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">Nog geen belletjes gelogd.</div>
            ) : recent.map((b) => (
              <Link key={b.id} to={`/verkoopkansen/${b.verkoopkans_id}`}
                className="block border border-border rounded-md p-2.5 hover:bg-secondary/40 transition-colors"
                data-testid={`recent-row-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <Badge variant={UITKOMST_VARIANT[b.uitkomst] || "outline"} className="text-xs">
                    {b.uitkomst === "deal" && <Trophy className="h-3 w-3 mr-1" />}
                    {UITKOMST_LABEL[b.uitkomst] || b.uitkomst}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</span>
                </div>
                <div className="text-sm font-medium mt-1 truncate">{b.organisatie_naam || b.kans_naam || "—"}</div>
                {b.notitie && <div className="text-xs text-muted-foreground mt-0.5 truncate">{b.notitie}</div>}
                <div className="text-xs text-muted-foreground mt-0.5">{b.eigenaar_naam}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
